/**
 * Supabase Backend Provider
 *
 * Implements AuthProvider, DataProvider, and ApiProvider using the
 * Supabase JS SDK. This is the default (and recommended) backend.
 *
 * Supabase provides:
 *   - PostgreSQL database with Row-Level Security
 *   - Built-in auth (email, OAuth, magic links, MFA)
 *   - Edge Functions (serverless, Deno runtime)
 *   - Realtime subscriptions
 *
 * All in one SDK with zero custom backend code.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/database";
import type {
  AuthProvider,
  AuthUser,
  AuthError,
  SignUpMetadata,
  DataProvider,
  DataResult,
  MutationResult,
  CreateMessageInput,
  BookClassInput,
  MemberEntitlements,
  ApiProvider,
  ApiResult,
  Backend,
} from "./types";
import type { Profile, Booking, ClassOccurrence, Membership, ClassPack, PublicScheduleRow, StudioStorefront } from "@/types/database";
import type { MyBookingRow, PublicLocationRow } from "./types";

// ---------------------------------------------------------------------------
// Supabase client singleton
// ---------------------------------------------------------------------------

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let client: SupabaseClient<Database> | null = null;

function getClient(): SupabaseClient<Database> {
  if (!client) {
    client = createClient<Database>(
      supabaseUrl || "https://placeholder.supabase.co",
      supabaseAnonKey || "placeholder-key",
      {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
      }
    );
  }
  return client;
}

function isConfigured(): boolean {
  return Boolean(
    supabaseUrl &&
      supabaseAnonKey &&
      !supabaseUrl.includes("placeholder") &&
      !supabaseAnonKey.includes("placeholder")
  );
}

/** Origin for auth emails / OAuth. Prefer live browser origin; fall back to env. */
function getAppOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  const fromEnv = import.meta.env.VITE_APP_URL as string | undefined;
  return (fromEnv || "http://localhost:8080").replace(/\/$/, "");
}

// ---------------------------------------------------------------------------
// Auth Provider
// ---------------------------------------------------------------------------

function mapUser(supabaseUser: { id: string; email?: string } | null): AuthUser | null {
  if (!supabaseUser) return null;
  return { id: supabaseUser.id, email: supabaseUser.email || "" };
}

/** Common Supabase Auth error strings, translated for a German-speaking studio. */
function translateAuthMessage(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("email rate limit exceeded")) {
    return "Zu viele E-Mails angefordert. Bitte warte etwas, bevor du es erneut versuchst.";
  }
  if (m.includes("for security purposes") && m.includes("after")) {
    return "Aus Sicherheitsgründen bitte kurz warten, bevor du das erneut anforderst.";
  }
  if (m.includes("invalid login credentials")) {
    return "E-Mail oder Passwort stimmt nicht.";
  }
  if (m.includes("email not confirmed")) {
    return "Diese E-Mail-Adresse wurde noch nicht bestätigt. Bitte prüfe dein Postfach.";
  }
  if (m.includes("user not found")) {
    return "Falls ein Konto mit dieser E-Mail existiert, hast du einen Link erhalten.";
  }
  if (m.includes("auth session missing")) {
    return "Der Reset-Link ist nicht mehr gültig. Bitte fordere einen neuen an.";
  }
  return message;
}

function mapError(err: unknown): AuthError | null {
  if (!err) return null;
  if (typeof err === "object" && err !== null && "message" in err) {
    const raw = (err as { message: string }).message;
    return { message: translateAuthMessage(raw) };
  }
  return { message: translateAuthMessage(String(err)) };
}

const supabaseAuth: AuthProvider = {
  async signInWithEmail(email, password) {
    const { data, error } = await getClient().auth.signInWithPassword({ email, password });
    return { user: mapUser(data?.user ?? null), error: mapError(error) };
  },

  async signUpWithEmail(email, password, metadata: SignUpMetadata) {
    const { data, error } = await getClient().auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        // Without this, Supabase falls back to Site URL (often still localhost).
        emailRedirectTo: `${getAppOrigin()}/auth/callback`,
      },
    });
    // A user without a session means Supabase is waiting for email confirmation.
    const requiresEmailConfirmation = Boolean(data?.user && !data?.session);
    return { error: mapError(error), requiresEmailConfirmation };
  },

  async signInWithOAuth(provider) {
    const { error } = await getClient().auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${getAppOrigin()}/auth/callback` },
    });
    return { error: mapError(error) };
  },

  async signOut() {
    await getClient().auth.signOut();
  },

  async resetPassword(email) {
    const { error } = await getClient().auth.resetPasswordForEmail(email, {
      redirectTo: `${getAppOrigin()}/auth/reset-confirm`,
    });
    return { error: mapError(error) };
  },

  async updatePassword(password) {
    const { error } = await getClient().auth.updateUser({ password });
    return { error: mapError(error) };
  },

  async getSession() {
    const { data } = await getClient().auth.getSession();
    return { user: mapUser(data?.session?.user ?? null) };
  },

  onAuthStateChange(callback) {
    const { data: { subscription } } = getClient().auth.onAuthStateChange((_event, session) => {
      callback(mapUser(session?.user ?? null));
    });
    return () => subscription.unsubscribe();
  },
};

// ---------------------------------------------------------------------------
// Data Provider
// ---------------------------------------------------------------------------

const supabaseData: DataProvider = {
  async getProfile(userId): Promise<DataResult<Profile>> {
    const client = getClient();
    // Roles live in studio_staff, not profiles. Resolve the caller's highest
    // studio role server-side so permission gating works in production. The
    // RPC always describes the *session* user, so only stamp it onto their
    // own profile — never onto another user's row.
    let profileRes = await client.from("profiles").select("*").eq("id", userId).maybeSingle();

    // If the signup trigger never created a row, create it now (RPC is
    // SECURITY DEFINER and only works for the session user).
    const sessionRes = await client.auth.getSession();
    const isOwnProfile = sessionRes.data?.session?.user?.id === userId;
    if ((!profileRes.data || profileRes.error) && isOwnProfile) {
      const ensured = await client.rpc("ensure_own_profile");
      if (!ensured.error && ensured.data) {
        profileRes = { data: ensured.data as Profile, error: null, count: null, status: 200, statusText: "OK" };
      } else {
        // Fallback: allow INSERT policy path
        const meta = sessionRes.data.session?.user?.user_metadata ?? {};
        const email = sessionRes.data.session?.user?.email ?? `${userId}@users.local`;
        await client.from("profiles").upsert({
          id: userId,
          email,
          first_name: (meta.first_name as string) || "",
          last_name: (meta.last_name as string) || "",
        });
        profileRes = await client.from("profiles").select("*").eq("id", userId).maybeSingle();
      }
    }

    const profile = profileRes.data as Profile | null;
    if (profileRes.error || !profile) {
      // Last resort: synthetic profile so auth UI / onboarding can proceed.
      if (isOwnProfile && sessionRes.data?.session?.user) {
        const u = sessionRes.data.session.user;
        const meta = u.user_metadata ?? {};
        return {
          data: {
            id: userId,
            email: u.email ?? `${userId}@users.local`,
            first_name: (meta.first_name as string) || "",
            last_name: (meta.last_name as string) || "",
            display_name: null,
            phone: null,
            avatar_url: null,
            role: "student",
          } as Profile,
          error: null,
        };
      }
      return { data: null, error: { message: profileRes.error?.message ?? "Profile not found" } };
    }

    const roleRes = await client.rpc("get_my_effective_role");
    if (roleRes.error) {
      console.warn("get_my_effective_role unavailable:", roleRes.error.message);
    }

    const role = isOwnProfile ? roleRes.data ?? "student" : profile.role;
    return { data: { ...profile, role }, error: null };
  },

  async createMessage(input: CreateMessageInput): Promise<MutationResult> {
    const { error } = await getClient().from("messages").insert({
      type: input.type,
      studio_id: input.studio_id || null,
      class_id: input.class_id || null,
      sender_id: input.sender_id || null,
      sender_name: input.sender_name || null,
      sender_email: input.sender_email || null,
      subject: input.subject,
      body: input.body,
      honeypot: input.honeypot || null,
    });

    return { error: error ? { message: error.message } : null };
  },

  async bookClass(input: BookClassInput): Promise<DataResult<Booking>> {
    const { data, error } = await getClient().rpc("book_class", {
      p_occurrence_id: input.occurrenceId,
      p_source_type: input.sourceType,
      p_source_id: input.sourceId ?? null,
    });

    return {
      data: (data as Booking) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async cancelBooking(bookingId): Promise<DataResult<Booking>> {
    const { data, error } = await getClient().rpc("cancel_booking", {
      p_booking_id: bookingId,
    });
    return {
      data: (data as Booking) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async getPublicSchedule(slug, limit = 12): Promise<DataResult<PublicScheduleRow[]>> {
    const { data, error } = await getClient().rpc("get_public_schedule", {
      p_slug: slug,
      p_limit: limit,
    });
    return {
      data: (data as PublicScheduleRow[]) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async getDefaultStudioSlug(): Promise<DataResult<string>> {
    const { data, error } = await getClient().rpc("get_default_studio_slug" as never);
    return {
      data: (data as string) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async getPublicLocations(slug): Promise<DataResult<PublicLocationRow[]>> {
    const { data, error } = await getClient().rpc("get_public_locations" as never, { p_slug: slug } as never);
    return {
      data: (data as PublicLocationRow[]) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async getMyBookings(): Promise<DataResult<MyBookingRow[]>> {
    const { data, error } = await getClient().rpc("get_my_bookings" as never);
    return {
      data: (data as MyBookingRow[]) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async getStudioStorefront(slug): Promise<DataResult<StudioStorefront>> {
    // The hand-written Database type doesn't satisfy supabase-js's rpc generic,
    // so args resolve to `never` (same as the other rpc calls here); assert.
    const { data, error } = await getClient().rpc("get_studio_storefront", { p_slug: slug } as never);
    return {
      data: (data as StudioStorefront) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async getUpcomingClasses(studioId): Promise<DataResult<ClassOccurrence[]>> {
    const { data, error } = await getClient()
      .from("class_occurrences")
      .select("*, offering:offerings(*), location:locations(*)")
      .eq("studio_id", studioId)
      .eq("is_cancelled", false)
      .gte("starts_at", new Date().toISOString())
      .order("starts_at", { ascending: true });

    return {
      data: (data as ClassOccurrence[]) ?? null,
      error: error ? { message: error.message } : null,
    };
  },

  async getMemberEntitlements(profileId, studioId): Promise<DataResult<MemberEntitlements>> {
    const client = getClient();
    const [membershipsRes, packsRes] = await Promise.all([
      client
        .from("memberships")
        .select("*, membership_type:membership_types(*)")
        .eq("profile_id", profileId)
        .eq("studio_id", studioId),
      client
        .from("class_packs")
        .select("*, class_pack_type:class_pack_types(*)")
        .eq("profile_id", profileId)
        .eq("studio_id", studioId),
    ]);

    const error = membershipsRes.error || packsRes.error;
    if (error) return { data: null, error: { message: error.message } };

    return {
      data: {
        memberships: (membershipsRes.data as Membership[]) ?? [],
        packs: (packsRes.data as ClassPack[]) ?? [],
      },
      error: null,
    };
  },
};

// ---------------------------------------------------------------------------
// API Provider (Edge Functions)
// ---------------------------------------------------------------------------

const supabaseApi: ApiProvider = {
  async invoke<T = unknown>(functionName: string, body: Record<string, unknown>): Promise<ApiResult<T>> {
    const { data, error } = await getClient().functions.invoke(functionName, { body });

    return {
      data: data as T | null,
      error: error ? { message: error.message } : null,
    };
  },
};

// ---------------------------------------------------------------------------
// Combined backend export
// ---------------------------------------------------------------------------

export const supabaseBackend: Backend = {
  auth: supabaseAuth,
  data: supabaseData,
  api: supabaseApi,
  isConfigured,
};
